import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Send,
  Paperclip,
  X,
  Loader2,
  Download,
  FileText,
  File as FileIcon,
  FileImage,
  Film,
  Music,
  Archive,
  Code,
  Inbox,
  FolderOpen,
} from "lucide-react";
import { ChevronDown } from "lucide-react";
import { TerminalTheme } from "../types";
import { linkifyText } from "../lib/linkify";
import { appendAuthToken } from "../lib/firebaseAuth";
import {
  getCurrentSubscription,
  getPushStatus,
  registerServiceWorker,
} from "../lib/push";
import { AgentDrawer } from "./AgentDrawer";
import { MobileDocViewerPane } from "./MobileDocViewerPane";

/* ─── Attachment marker parsing + rendering ─────────────────────────────
 * Both sides of the conversation (user uploads, agent file deliveries) use
 * the same marker: `[attachment: <path> (<size>)]`. Parse them out of bubble
 * text so we can render images as inline thumbnails and files as download
 * chips instead of showing the raw marker text. Paths under
 * `state/uploads/chat/` resolve to the container's `/api/uploads/chat/<name>`
 * static-file route, which works over Tailscale automatically since the
 * dashboard is what's served via Tailscale Serve.
 */

interface ParsedAttachment {
  path: string;
  name: string;
  size: string;
  url: string;
}

const ATTACHMENT_RE = /\[attachment:\s*([^\s\]]+)\s+\(([^)]+)\)\]/g;

// ─── Local profile + hidden-sessions storage (PWA /chat layer) ──────────
// Local profile + session storage. Pure localStorage — per-device-install,
// no server side. Profile = operator's display name + avatar shown on their
// own bubbles; hidden sessions declutter the session list after repeated
// Home-Screen adds.
const CHAT_PROFILE_KEY = "chat-user-profile-v1";
const CHAT_HIDDEN_SESSIONS_KEY = "chat-hidden-sessions-v1";

interface ChatUserProfile {
  displayName: string;
  avatarDataUrl: string | null;
}

function loadChatProfile(): ChatUserProfile {
  try {
    const raw = localStorage.getItem(CHAT_PROFILE_KEY);
    if (raw) {
      const p = JSON.parse(raw);
      return { displayName: p.displayName || "Operator", avatarDataUrl: p.avatarDataUrl || null };
    }
  } catch {}
  return { displayName: "Operator", avatarDataUrl: null };
}
function saveChatProfile(p: ChatUserProfile) {
  try { localStorage.setItem(CHAT_PROFILE_KEY, JSON.stringify(p)); } catch {}
}
function loadHiddenSessions(): string[] {
  try {
    const raw = localStorage.getItem(CHAT_HIDDEN_SESSIONS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}
function saveHiddenSessions(keys: string[]) {
  try { localStorage.setItem(CHAT_HIDDEN_SESSIONS_KEY, JSON.stringify(keys)); } catch {}
}

// Per-bubble timestamp. Same-day → time only; older → "Mon D, time".
function formatBubbleTime(ts: string): string {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return "";
    const now = new Date();
    const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    if (d.toDateString() === now.toDateString()) return time;
    return `${d.toLocaleDateString([], { month: "short", day: "numeric" })}, ${time}`;
  } catch {
    return "";
  }
}

// Doc-ref auto-pill — mirrors AgentChatTerminal's DOC_REF_RE. Detects
// state/documents/foo.md (and subdirs like state/documents/drafts/bar.md)
// in agent prose so we can render a tappable pill on mobile too.
const DOC_REF_RE = /\bstate\/documents\/(?:[\w\-/.]+\/)?([\w\-.]+\.md)\b/g;

function parseAttachments(text: unknown): { prose: string; attachments: ParsedAttachment[] } {
  // Defensive: API contract says `text` is a string, but a bad envelope (null,
  // missing key, number) must NOT crash the whole conversation render — mobile
  // PWA shows no console, so a thrown error here looks like "the chat just
  // disappears." Coerce to string, never throw.
  const safe = typeof text === "string" ? text : "";
  const attachments: ParsedAttachment[] = [];
  let m: RegExpExecArray | null;
  ATTACHMENT_RE.lastIndex = 0;
  while ((m = ATTACHMENT_RE.exec(safe)) !== null) {
    const path = m[1];
    const name = path.split("/").pop() || path;
    const url = path.startsWith("state/uploads/chat/")
      ? appendAuthToken(`/api/uploads/chat/${encodeURIComponent(name)}`)
      : path; // best effort fallback; non-uploads/chat paths may 404
    attachments.push({ path, name, size: m[2], url });
  }
  const prose = safe.replace(ATTACHMENT_RE, "").trim();
  return { prose, attachments };
}

/** Wrap linkifyText to ALSO render doc-ref pills (state/documents/foo.md
 *  patterns). Splits the prose into ordered chunks of {text} and {doc-pill}
 *  so the operator can tap any reference Margo emits and open the file in
 *  a full-screen MobileDocViewerPane. */
function renderProseWithDocPills(
  prose: string,
  theme: TerminalTheme,
  keyPrefix: string,
  onDocClick?: (filename: string) => void,
): React.ReactNode[] {
  if (!onDocClick) {
    return [linkifyText(prose, keyPrefix)];
  }
  const parts: React.ReactNode[] = [];
  DOC_REF_RE.lastIndex = 0;
  let last = 0; let i = 0; let m: RegExpExecArray | null;
  while ((m = DOC_REF_RE.exec(prose)) !== null) {
    if (m.index > last) {
      parts.push(<React.Fragment key={`${keyPrefix}t${i}`}>{linkifyText(prose.slice(last, m.index), `${keyPrefix}t${i}-`)}</React.Fragment>);
    }
    const filename = m[1];
    parts.push(
      <button
        key={`${keyPrefix}p${i}`}
        type="button"
        onClick={() => onDocClick(filename)}
        style={{
          color: theme.bg,
          backgroundColor: theme.accent,
          borderColor: theme.accent,
        }}
        className="inline-flex items-center gap-1 mx-0.5 px-2 py-0.5 rounded text-[12px] font-bold align-baseline"
      >
        📄 {filename}
      </button>
    );
    last = m.index + m[0].length;
    i++;
  }
  if (last < prose.length) {
    parts.push(<React.Fragment key={`${keyPrefix}t${i}`}>{linkifyText(prose.slice(last), `${keyPrefix}t${i}-`)}</React.Fragment>);
  }
  return parts.length ? parts : [linkifyText(prose, keyPrefix)];
}

function isImagePath(path: string): boolean {
  return /\.(jpe?g|png|gif|webp|svg|heic|heif|bmp)$/i.test(path);
}

function iconForPath(path: string, size: number = 18) {
  if (isImagePath(path)) return <FileImage size={size} />;
  if (/\.(mp4|mov|webm|mkv|avi)$/i.test(path)) return <Film size={size} />;
  if (/\.(mp3|wav|ogg|m4a|flac)$/i.test(path)) return <Music size={size} />;
  if (/\.(zip|tar|gz|bz2|7z|rar)$/i.test(path)) return <Archive size={size} />;
  if (/\.(py|js|ts|tsx|jsx|sh|rb|go|rs|java|c|cpp|h|html|css|json|yaml|yml)$/i.test(path))
    return <Code size={size} />;
  if (/\.(pdf|doc|docx|txt|md|rtf|log|csv|tsv)$/i.test(path)) return <FileText size={size} />;
  return <FileIcon size={size} />;
}

/** Render one attachment — inline image thumb or download chip. */
function isMarkdownPath(path: string): boolean {
  return /\.(md|markdown)(?:\?|$)/i.test(path);
}

function AttachmentBlock({
  att,
  theme,
  scopedFetch,
  onOpenMd,
}: {
  att: ParsedAttachment;
  theme: TerminalTheme;
  scopedFetch?: (path: string, init?: RequestInit) => Promise<Response>;
  onOpenMd?: (url: string, name: string) => void;
}) {
  // For scopedFetch (fleet-aware peer), we need to route the URL through
  // the same path. agentScopedUrl is doing it for fetches; for an anchor
  // href we apply the same prefix manually when needed. Simplest path:
  // resolve relative URL — the browser already knows the dashboard host.
  const href = att.url;

  // .md attachments open in the in-app markdown viewer — iOS renders raw
  // .md as ugly plaintext, so we never hand the browser the raw file.
  if (isMarkdownPath(att.path) && onOpenMd) {
    return (
      <button
        type="button"
        onClick={() => onOpenMd(att.url, att.name)}
        style={{ borderColor: theme.border, backgroundColor: theme.bg, color: theme.fg }}
        className="flex items-center gap-3 border rounded-lg px-3 py-2.5 w-full text-left hover:opacity-90 transition-opacity"
      >
        <span style={{ color: theme.accent }} className="flex-shrink-0">
          {iconForPath(att.path, 22)}
        </span>
        <div className="flex-1 min-w-0">
          <div className="truncate" style={{ fontSize: "14px" }}>{att.name}</div>
          <div style={{ fontSize: "11px", color: theme.systemText }}>{att.size} · tap to read</div>
        </div>
        <FileText size={16} style={{ color: theme.systemText }} className="flex-shrink-0" />
      </button>
    );
  }

  if (isImagePath(att.path)) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{ borderColor: theme.border }}
        className="block border rounded-lg overflow-hidden"
        aria-label={`Open ${att.name}`}
      >
        <img
          src={href}
          alt={att.name}
          style={{ maxHeight: "260px", display: "block" }}
          className="w-full h-auto object-cover"
          loading="lazy"
        />
      </a>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      download={att.name}
      style={{
        borderColor: theme.border,
        backgroundColor: theme.bg,
        color: theme.fg,
      }}
      className="flex items-center gap-3 border rounded-lg px-3 py-2.5 no-underline hover:opacity-90 transition-opacity"
    >
      <span style={{ color: theme.accent }} className="flex-shrink-0">
        {iconForPath(att.path, 22)}
      </span>
      <div className="flex-1 min-w-0">
        <div className="truncate" style={{ fontSize: "14px" }}>
          {att.name}
        </div>
        <div style={{ fontSize: "11px", color: theme.systemText }}>{att.size}</div>
      </div>
      <Download size={16} style={{ color: theme.systemText }} className="flex-shrink-0" />
    </a>
  );
}

/**
 * MobileChatTerminal — purpose-built chat UI for the Tailscale-companion
 * /chat route. Mobile-first; bigger fonts, edge-to-edge, iMessage-style
 * bubbles, pinned input bar.
 *
 *   ┌────────────────────────────────┐
 *   │ [Session ▾]                    │  ← slim session selector header
 *   ├────────────────────────────────┤
 *   │           bubble bubble        │  ← message thread, auto-scrolls
 *   │  bubble                        │
 *   ├────────────────────────────────┤
 *   │ [📎 photo.jpg ✕] [📎 doc.pdf ✕]│  ← pending attachment chips (if any)
 *   │ [📎]  [Message Margo…]   [▷]   │  ← attach button + textarea + send
 *   └────────────────────────────────┘
 *
 * Critical mobile UX:
 *   • textarea font-size: 16px (≥16 = no iOS Safari auto-zoom on focus)
 *   • Send button + attach button = 44×44pt min (iOS HIG)
 *   • Auto-resize textarea up to 200px then scrolls
 *   • Enter sends, Shift+Enter newline; enterKeyHint=send
 *   • Smooth auto-scroll to latest message
 *
 * Features (parity with desktop AgentChatTerminal):
 *   • Polls GET /api/messages/conversation?channel=<key>&n=200 every 3s
 *   • Posts user messages to POST /api/inbound { text, principal }
 *   • Files via POST /api/uploads/chat (multipart) → server returns
 *     {path, name, size, content_type}. Path embedded in the user's text
 *     as `[attachment: <relpath> (<size>)]` so the agent reads the file
 *     via its own filesystem.
 *   • Sessions list via GET /api/messages/sessions; lets the operator
 *     browse other channels' threads (telegram, slack, imessage).
 *     Currently view-only — send always goes through dashboard channel
 *     (POST /api/inbound). Operator-takeover send-out-as-agent is a
 *     desktop power-user feature kept in AgentChatTerminal for now.
 */

interface ChatMessage {
  role: "user" | "agent";
  filename: string;
  label?: string;
  text: string;
  ts: string;
  mtime: number;
  via_operator?: boolean;
}

interface Session {
  key: string;
  channel: string;
  chat_id: string | number | null;
  peer: string | null;
  label: string;
  last_text: string;
  last_ts: string;
  message_count: number;
}

interface Attachment {
  path: string;
  name: string;
  size: number;
  content_type: string;
}

interface MobileChatTerminalProps {
  theme: TerminalTheme;
  agentLabel?: string;
  scopedFetch?: (path: string, init?: RequestInit) => Promise<Response>;
  canSend?: boolean;
  principal?: string;
  /** Theme selection lifted from the host page so the in-drawer theme
   *  picker can live inside the chat surface (iOS Messages × FaceTime
   *  info-panel pattern). When unset, theme controls hidden in drawer. */
  selectedThemeId?: string;
  onSelectTheme?: (id: string) => void;
  /** Tap callback when the user taps an auto-detected doc-ref pill in an
   *  agent message — parent surfaces MobileDocViewerPane. */
  onDocClick?: (filename: string) => void;
  /** Optional drawer entries provided by ChatOnlyPage — open the docs
   *  drawer / outbound-preview drafts drawer from the chat header. */
  onOpenDocs?: () => void;
  onOpenDrafts?: () => void;
}

const POLL_INTERVAL_MS = 3000;
const SESSION_POLL_INTERVAL_MS = 12000;
const MAX_TEXTAREA_HEIGHT_PX = 200;
const DASHBOARD_KEY = "dashboard";

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export const MobileChatTerminal: React.FC<MobileChatTerminalProps> = ({
  theme,
  agentLabel = "Agent",
  scopedFetch,
  canSend = true,
  principal = "Operator",
  selectedThemeId,
  onSelectTheme,
  onDocClick,
  onOpenDocs,
  onOpenDrafts,
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [selectedSessionKey, setSelectedSessionKey] = useState<string>(DASHBOARD_KEY);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [atBottom, setAtBottom] = useState(true);
  // In-app markdown viewer for tapped .md attachments (chat uploads).
  const [mdViewer, setMdViewer] = useState<{ url: string; name: string } | null>(null);
  // PWA-local profile + hidden sessions (Craig parity).
  const [userProfile, setUserProfile] = useState<ChatUserProfile>(() => loadChatProfile());
  const [hiddenSessions, setHiddenSessions] = useState<string[]>(() => loadHiddenSessions());
  const scrollEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Scroll-to-bottom arrow — IntersectionObserver on the end-anchor flips
  // `atBottom`. Arrow only renders when scrolled up; tap → smooth scroll.
  useEffect(() => {
    const target = scrollEndRef.current;
    if (!target) return;
    const obs = new IntersectionObserver(
      ([entry]) => setAtBottom(entry.isIntersecting),
      { threshold: 0, rootMargin: "0px" },
    );
    obs.observe(target);
    return () => obs.disconnect();
  }, []);

  // No per-bubble copy button — operators select text directly (bubbles are
  // `select-text`), which they preferred over the corner copy affordance.

  const fetcher: (path: string, init?: RequestInit) => Promise<Response> =
    scopedFetch || ((path, init) => fetch(path, init));

  const isDashboardSession = selectedSessionKey === DASHBOARD_KEY;

  // ─── Push state + Agent drawer ──────────────────────────────────────
  // Push subscription state is shown as a small dot on the header avatar
  // (subscribed vs not). Actual subscribe/unsubscribe toggle lives in the
  // drawer's Settings tab (iOS Messages × FaceTime info-panel pattern).
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      await registerServiceWorker();
      const sub = await getCurrentSubscription();
      if (cancelled) return;
      setPushSubscribed(!!sub);
    };
    refresh();
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  // Avatar source — try a deployment-supplied logo, fall back to the
  // agent's first initial on a colored circle.
  const [avatarSrc, setAvatarSrc] = useState<string | null>(
    "/static-next/agent-logo.png"
  );
  const handleAvatarError = useCallback(() => {
    setAvatarSrc((prev) =>
      prev === "/static-next/agent-logo.png" ? "/static-next/agent-logo.svg" : null
    );
  }, []);

  // ─── Drafts-pending badge ───────────────────────────────────────────
  // Background-poll /api/outbound-preview/pending every 8s so the header
  // shows a live count of holds awaiting Leah's release/edit/kill. Only
  // wires up when the parent provided onOpenDrafts (avoids burning a
  // poll for a button that's not rendered).
  const [pendingDraftCount, setPendingDraftCount] = useState(0);
  useEffect(() => {
    if (!onOpenDrafts) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await fetcher("/api/outbound-preview/pending");
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const n = Array.isArray(data?.items) ? data.items.length : 0;
        if (!cancelled) setPendingDraftCount(n);
      } catch {
        /* silent — next tick will retry */
      }
    };
    poll();
    const id = window.setInterval(poll, 8000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [fetcher, onOpenDrafts]);

  // ─── Poll: conversation thread ──────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams({ n: "200" });
    if (isDashboardSession) {
      params.set("channel", "dashboard");
    } else {
      const s = sessions.find((x) => x.key === selectedSessionKey);
      if (s) {
        params.set("channel", s.channel);
        if (s.chat_id != null) params.set("chat_id", String(s.chat_id));
      }
    }
    const url = `/api/messages/conversation?${params.toString()}`;

    async function poll() {
      try {
        const res = await fetcher(url);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const msgs: ChatMessage[] = Array.isArray(data)
          ? data
          : data.messages || [];
        if (!cancelled) setMessages(msgs);
      } catch {
        /* silent — next tick will retry */
      }
    }

    poll();
    const id = window.setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [fetcher, selectedSessionKey, sessions, isDashboardSession]);

  // ─── Auto-scroll to newest ──────────────────────────────────────────
  // When messages load (including the FIRST entry into the app) or a new
  // message arrives, follow to the bottom IF the user hasn't scrolled up to
  // read history. `atBottom` defaults true, so opening the app lands on the
  // latest message. The chevron button jumps back down when scrolled up.
  // (scroll-to-bottom helper — an earlier copy lacked it,
  // which is why /chat stopped landing at the bottom on entry.)
  useEffect(() => {
    if (messages.length === 0) return;
    if (atBottom) {
      scrollEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [messages.length, atBottom]);

  // ─── Poll: sessions list (slower cadence) ───────────────────────────
  useEffect(() => {
    let cancelled = false;
    async function poll() {
      try {
        const res = await fetcher("/api/messages/sessions");
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const rows: Session[] = Array.isArray(data?.sessions) ? data.sessions : [];
        if (!cancelled) setSessions(rows);
      } catch {
        /* silent */
      }
    }
    poll();
    const id = window.setInterval(poll, SESSION_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [fetcher]);

  // No auto-scroll. Operators read the thread at their own pace; auto-
  // scrolling on load / on every new message (incl. background polls) used
  // to yank them to the bottom and lose their place. The chevron button
  // (bottom-right) lets them jump to the latest on demand instead.

  // ─── File upload ─────────────────────────────────────────────────────
  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;
      setUploadError(null);
      setUploading(true);
      try {
        for (const file of Array.from(files)) {
          const fd = new FormData();
          fd.append("file", file);
          const res = await fetcher("/api/uploads/chat", {
            method: "POST",
            body: fd,
          });
          if (!res.ok) {
            const detail = await res
              .json()
              .then((d: any) => d.detail || `HTTP ${res.status}`)
              .catch(() => `HTTP ${res.status}`);
            setUploadError(`${file.name}: ${detail}`);
            continue;
          }
          const meta = (await res.json()) as Attachment;
          setAttachments((prev) => [...prev, meta]);
        }
      } finally {
        setUploading(false);
        // Reset the input so the SAME file can be re-selected later if needed
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    },
    [fetcher]
  );

  const onFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFiles(e.target.files);
    },
    [handleFiles]
  );

  const removeAttachment = useCallback((index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Auto-resize textarea
  const handleInput = useCallback((e: React.FormEvent<HTMLTextAreaElement>) => {
    const ta = e.currentTarget;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, MAX_TEXTAREA_HEIGHT_PX)}px`;
    setDraft(ta.value);
  }, []);

  // ─── Send ────────────────────────────────────────────────────────────
  const send = useCallback(async () => {
    const text = draft.trim();
    if ((!text && attachments.length === 0) || sending || !canSend) return;
    setSending(true);
    try {
      // Build the wire-text: original message + attachment markers so the
      // agent (Margo / Craig / etc.) can Read each file via its own
      // filesystem from the path embedded in the marker.
      const lines = text ? [text] : [];
      for (const a of attachments) {
        lines.push(`[attachment: ${a.path} (${formatBytes(a.size)})]`);
      }
      const wire = lines.join("\n");

      const effectivePrincipal = (userProfile.displayName || principal || "Operator").trim();
      await fetcher("/api/inbound", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: wire, principal: effectivePrincipal }),
      });

      setDraft("");
      setAttachments([]);
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
        textareaRef.current.focus();
      }

      // Refresh thread + sessions immediately so the new message + any
      // bumped session are visible without waiting for the next tick.
      const params = new URLSearchParams({ n: "200", channel: "dashboard" });
      const res = await fetcher(
        `/api/messages/conversation?${params.toString()}`
      );
      if (res.ok) {
        const data = await res.json();
        const msgs: ChatMessage[] = Array.isArray(data)
          ? data
          : data.messages || [];
        setMessages(msgs);
      }
    } catch {
      /* silent — user can retry by hitting send again */
    } finally {
      setSending(false);
    }
  }, [draft, attachments, sending, canSend, fetcher, principal, userProfile]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        send();
      }
    },
    [send]
  );

  const canSubmit =
    (draft.trim().length > 0 || attachments.length > 0) && !sending && canSend;

  // Available sessions for the dropdown — always show dashboard first.
  // Locally-hidden sessions (operator archived them in the drawer) are
  // filtered out.
  const sessionsForDropdown: Array<{ key: string; label: string; count: number }> = [
    { key: DASHBOARD_KEY, label: `Operator chat · ${agentLabel}`, count: 0 },
    ...sessions
      .filter((s) => s.key !== DASHBOARD_KEY && !hiddenSessions.includes(s.key))
      .map((s) => ({ key: s.key, label: s.label, count: s.message_count })),
  ];

  // Show the session switcher only when there's something other than the
  // dashboard channel — power-user feature, hidden by default on mobile.
  const hasMultipleSessions = sessions.filter(
    (s) => s.key !== DASHBOARD_KEY && !hiddenSessions.includes(s.key)
  ).length > 0;

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: theme.bg }}>
      {/* iOS Messages × FaceTime info-panel header — single 56px bar.
          Tap the avatar OR the name to open the agent drawer. */}
      <div
        style={{
          borderBottomColor: theme.border,
          backgroundColor: theme.inputBg,
        }}
        className="border-b px-3 py-2 flex items-center gap-3 flex-shrink-0"
      >
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="flex items-center gap-3 flex-1 min-w-0 text-left"
          aria-label={`Open ${agentLabel} info`}
        >
          <div className="relative flex-shrink-0">
            <div
              style={{
                backgroundColor: theme.accent,
                color: theme.bg,
                width: 40,
                height: 40,
              }}
              className="rounded-full flex items-center justify-center font-bold text-base overflow-hidden"
            >
              {avatarSrc ? (
                <img
                  src={avatarSrc}
                  alt={`${agentLabel} logo`}
                  onError={handleAvatarError}
                  className="h-full w-full object-cover"
                />
              ) : (
                agentLabel.slice(0, 1).toUpperCase()
              )}
            </div>
            {pushSubscribed && (
              <div
                style={{
                  backgroundColor: "#10b981",
                  borderColor: theme.inputBg,
                }}
                className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2"
                title="Push notifications enabled"
              />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div
              style={{ color: theme.fg }}
              className="font-semibold text-[16px] leading-tight truncate"
            >
              {agentLabel}
            </div>
            <div
              style={{ color: theme.systemText }}
              className="text-[11px] truncate"
            >
              {!isDashboardSession ? "Viewing another channel" : "Tap for info, files & settings"}
            </div>
          </div>
        </button>
        {/* Docs shortcut — opens MobileDocsDrawer (Pinned + Recent). Mobile
            sibling of the desktop Documents tab; tap-to-view, no drill-in. */}
        {onOpenDocs && (
          <button
            type="button"
            onClick={onOpenDocs}
            style={{
              color: theme.systemText,
              borderColor: theme.border,
              minWidth: 40,
              minHeight: 40,
            }}
            className="border rounded-full flex items-center justify-center flex-shrink-0"
            aria-label="Open Documents"
            title="Documents"
          >
            <FolderOpen size={16} />
          </button>
        )}
        {/* Drafts-pending pill — when Margo is holding outbound for Leah's
            review, tap to open the mobile drafts drawer. Hidden when the
            queue is empty or no onOpenDrafts handler is wired. */}
        {onOpenDrafts && pendingDraftCount > 0 && (
          <button
            type="button"
            onClick={() => onOpenDrafts()}
            style={{
              backgroundColor: `${theme.accent}22`,
              borderColor: `${theme.accent}66`,
              color: theme.accent,
              minHeight: "40px",
            }}
            className="border rounded-full px-3 flex items-center gap-1.5 flex-shrink-0 font-semibold text-[13px]"
            aria-label={`Open ${pendingDraftCount} pending drafts`}
            title="Drafts pending your release"
          >
            <Inbox size={15} />
            <span className="tabular-nums">{pendingDraftCount}</span>
          </button>
        )}
        {/* Session switcher — only shown when sessions exist beyond the
            dashboard channel (intervene-mode power users). */}
        {hasMultipleSessions && (
          <div className="relative flex-shrink-0">
            <select
              value={selectedSessionKey}
              onChange={(e) => setSelectedSessionKey(e.target.value)}
              style={{
                color: theme.systemText,
                backgroundColor: "transparent",
                borderColor: theme.border,
                fontSize: "12px",
              }}
              className="border rounded-lg px-2 pr-7 py-1.5 truncate focus:outline-none appearance-none max-w-[140px]"
              aria-label="Conversation thread"
            >
              {sessionsForDropdown.map((s) => (
                <option key={s.key} value={s.key} style={{ backgroundColor: theme.bg, color: theme.fg }}>
                  {s.label}
                  {s.count > 0 ? ` · ${s.count}` : ""}
                </option>
              ))}
            </select>
            <ChevronDown
              size={12}
              style={{ color: theme.systemText }}
              className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
            />
          </div>
        )}
      </div>

      {/* Message thread — claims all space between header + input */}
      <div
        className="flex-1 overflow-y-auto px-3 py-4 min-h-0"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {messages.length === 0 ? (
          <div
            className="text-center py-16 px-6"
            style={{ color: theme.systemText }}
          >
            <p style={{ fontSize: "15px", lineHeight: "1.5" }}>
              {isDashboardSession ? (
                <>Say hi to <span style={{ color: theme.fg }}>{agentLabel}</span>.</>
              ) : (
                <>No history in this thread yet.</>
              )}
            </p>
            <p
              className="mt-2"
              style={{ fontSize: "13px", color: theme.systemText, opacity: 0.7 }}
            >
              Messages sync every few seconds. Replies appear here.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {messages.map((m, i) => {
              const isUser = m.role === "user";
              const { prose, attachments } = parseAttachments(m.text);
              return (
                <div
                  key={m.filename || `${m.ts}-${i}`}
                  className={`flex ${isUser ? "justify-end" : "justify-start"}`}
                >
                  <div
                    style={{
                      backgroundColor: isUser ? `${theme.accent}28` : theme.inputBg,
                      borderColor: isUser ? `${theme.accent}55` : theme.border,
                      color: theme.fg,
                      fontSize: "15px",
                      lineHeight: "1.45",
                      borderRadius: isUser
                        ? "18px 18px 4px 18px"
                        : "18px 18px 18px 4px",
                    }}
                    className="relative border px-3.5 py-2.5 max-w-[85%] break-words select-text"
                  >
                    {prose && (
                      <div
                        className="whitespace-pre-wrap"
                        style={{
                          marginBottom: attachments.length > 0 ? "8px" : 0,
                        }}
                      >
                        {renderProseWithDocPills(prose, theme, `mob-${i}-`, onDocClick)}
                      </div>
                    )}
                    {attachments.length > 0 && (
                      <div className="flex flex-col gap-2">
                        {attachments.map((a, ai) => (
                          <AttachmentBlock
                            key={`${a.path}-${ai}`}
                            att={a}
                            theme={theme}
                            scopedFetch={scopedFetch}
                            onOpenMd={(url, name) => setMdViewer({ url, name })}
                          />
                        ))}
                      </div>
                    )}
                    {m.ts && (
                      <div
                        style={{ color: theme.systemText, fontSize: "10px", opacity: 0.7 }}
                        className={`mt-1.5 ${isUser ? "text-right" : "text-left"}`}
                      >
                        {formatBubbleTime(m.ts)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            <div ref={scrollEndRef} />
          </div>
        )}
        {/* Scroll-to-bottom arrow — sticky-positioned so it tracks the
            scroll edge inside the messages container, not the viewport.
            Sized larger than desktop for thumb-reach. */}
        {!atBottom && (
          <div className="sticky bottom-3 flex justify-center pointer-events-none -mt-12 z-10">
            <button
              type="button"
              onClick={() => scrollEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" })}
              style={{
                backgroundColor: theme.accent,
                color: theme.bg,
                boxShadow: `0 4px 14px ${theme.accent}55`,
              }}
              className="pointer-events-auto rounded-full p-2.5"
              aria-label="Scroll to latest"
            >
              <ChevronDown size={18} />
            </button>
          </div>
        )}
      </div>

      {/* Pending attachment chips — rendered above the input when present */}
      {(attachments.length > 0 || uploadError) && (
        <div
          style={{
            borderTopColor: theme.border,
            backgroundColor: theme.inputBg,
          }}
          className="border-t px-3 py-2 flex-shrink-0"
        >
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {attachments.map((a, i) => (
                <div
                  key={`${a.path}-${i}`}
                  style={{
                    backgroundColor: theme.bg,
                    borderColor: theme.border,
                    color: theme.fg,
                  }}
                  className="border rounded-full pl-3 pr-1 py-1 flex items-center gap-2 text-[13px]"
                >
                  <Paperclip size={12} style={{ color: theme.accent }} />
                  <span className="truncate max-w-[140px]">{a.name}</span>
                  <span style={{ color: theme.systemText }} className="text-[11px] flex-shrink-0">
                    {formatBytes(a.size)}
                  </span>
                  <button
                    onClick={() => removeAttachment(i)}
                    style={{ color: theme.systemText }}
                    className="rounded-full p-1 hover:opacity-70 flex-shrink-0"
                    aria-label={`Remove ${a.name}`}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
          {uploadError && (
            <div style={{ color: "#f43f5e" }} className="text-[12px] mt-1.5 flex items-center gap-1.5">
              <X size={12} />
              {uploadError}
            </div>
          )}
        </div>
      )}

      {/* Input bar — attach button + textarea + send button */}
      <div
        style={{
          borderTopColor: theme.border,
          backgroundColor: theme.inputBg,
        }}
        className="border-t px-3 py-2 flex items-end gap-2 flex-shrink-0"
      >
        {/* Attach button + hidden file input */}
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={!canSend || uploading}
          style={{
            backgroundColor: theme.bg,
            borderColor: theme.border,
            color: theme.accent,
            minWidth: "44px",
            minHeight: "44px",
          }}
          className="border rounded-full flex items-center justify-center flex-shrink-0 disabled:opacity-50"
          aria-label="Attach file"
          title="Attach file"
        >
          {uploading ? <Loader2 size={18} className="animate-spin" /> : <Paperclip size={18} />}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={onFileInputChange}
          className="hidden"
          aria-hidden="true"
        />

        <textarea
          ref={textareaRef}
          value={draft}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          placeholder={canSend ? `Message ${agentLabel}…` : "Read-only mode"}
          disabled={!canSend || sending}
          rows={1}
          autoComplete="off"
          autoCorrect="on"
          spellCheck={true}
          enterKeyHint="send"
          style={{
            backgroundColor: theme.bg,
            color: theme.fg,
            borderColor: theme.border,
            fontSize: "16px",
            lineHeight: "1.4",
            resize: "none",
            maxHeight: `${MAX_TEXTAREA_HEIGHT_PX}px`,
          }}
          className="flex-1 border rounded-2xl px-4 py-2.5 focus:outline-none disabled:opacity-50"
          aria-label="Message input"
        />

        <button
          onClick={send}
          disabled={!canSubmit}
          style={{
            backgroundColor: canSubmit ? theme.accent : `${theme.accent}40`,
            color: canSubmit ? theme.bg : `${theme.bg}80`,
            minWidth: "44px",
            minHeight: "44px",
          }}
          className="rounded-full flex items-center justify-center flex-shrink-0 transition-opacity disabled:cursor-not-allowed"
          aria-label="Send message"
        >
          {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
        </button>
      </div>

      {/* Agent info drawer — slide-up bottom sheet (iOS Messages info pattern).
          Owns notifications toggle, theme switcher, files/links/photos browser,
          export thread. Opens when operator taps the header avatar/name. */}
      <AgentDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        theme={theme}
        agentLabel={agentLabel}
        messages={messages.map((m) => ({
          role: m.role,
          text: m.text,
          ts: m.ts,
          filename: m.filename,
        }))}
        fetcher={fetcher}
        selectedThemeId={selectedThemeId || ""}
        onSelectTheme={onSelectTheme || (() => {})}
        userProfile={userProfile}
        onUpdateProfile={(p) => { setUserProfile(p); saveChatProfile(p); }}
        hiddenSessions={hiddenSessions}
        onHideSession={(key) => {
          const next = Array.from(new Set([...hiddenSessions, key]));
          setHiddenSessions(next);
          saveHiddenSessions(next);
        }}
        onResetLocalData={() => {
          try {
            localStorage.removeItem(CHAT_PROFILE_KEY);
            localStorage.removeItem(CHAT_HIDDEN_SESSIONS_KEY);
          } catch {}
          setUserProfile({ displayName: "Operator", avatarDataUrl: null });
          setHiddenSessions([]);
          window.location.reload();
        }}
      />

      {mdViewer && (
        <MobileDocViewerPane
          filename={mdViewer.name}
          rawUrl={mdViewer.url}
          theme={theme}
          scopedFetch={scopedFetch}
          onClose={() => setMdViewer(null)}
        />
      )}
    </div>
  );
};
